import apiClient from "./client";

/**
 * Distribution Rules API Service
 */
export const distributionRulesApi = {
  /**
   * Get all distribution rules
   */
  getAll: (params = {}) => {
    return apiClient.get("/distributionrules", params);
  },

  /**
   * Get a distribution rule by ID
   */
  getById: (id) => {
    return apiClient.get(`/distributionrules/${id}`);
  },

  /**
   * Get the active rule for a location
   */
  getActive: (location = null) => {
    return apiClient.get(
      "/distributionrules/active",
      location ? { location } : {},
    );
  },

  /**
   * Get auto-calculated allocations based on job role TroncScore
   * Used for ByJobRoleScore distribution type
   */
  getScoreAllocations: () => {
    return apiClient.get("/distributionrules/score-allocations");
  },

  /**
   * Create a new distribution rule
   */
  create: (rule) => {
    return apiClient.post("/distributionrules", rule);
  },

  /**
   * Update a distribution rule
   */
  update: (id, rule) => {
    return apiClient.put(`/distributionrules/${id}`, rule);
  },

  /**
   * Update only name and description (no version required)
   */
  updateMetadata: (id, data) => {
    return apiClient.patch(`/distributionrules/${id}/metadata`, data);
  },

  /**
   * Activate a distribution rule
   * Only TenantAdmin or higher can activate
   * @param {string} id - Rule ID
   * @param {Date} effectiveFrom - Effective date (must be in the future)
   */
  activate: (id, effectiveFrom) => {
    return apiClient.post(`/distributionrules/${id}/activate`, {
      effectiveFrom: effectiveFrom.toISOString(),
    });
  },

  /**
   * Deactivate a distribution rule
   */
  deactivate: (id, reason = null) => {
    return apiClient.post(`/distributionrules/${id}/deactivate`, {
      reason,
    });
  },

  /**
   * Delete a distribution rule
   * Cannot delete rules that have been used in distributions
   */
  delete: (id, reason = null) => {
    return apiClient.delete(
      `/distributionrules/${id}`,
      reason ? { reason } : {},
    );
  },

  /**
   * Preview a distribution calculation
   * @param {string} ruleId - Distribution rule ID
   * @param {number} totalAmount - Total amount to distribute
   * @param {string} [locationId] - Optional location ID filter
   * @param {string} [periodStart] - Optional period start date (ISO format)
   * @param {string} [periodEnd] - Optional period end date (ISO format)
   */
  preview: (ruleId, totalAmount, locationId = null, periodStart = null, periodEnd = null) => {
    const body = {
      ruleId,
      totalAmount,
      locationId,
    };
    if (periodStart) body.periodStart = periodStart;
    if (periodEnd) body.periodEnd = periodEnd;
    return apiClient.post("/distributionrules/preview", body);
  },

  /**
   * Validate a distribution rule configuration
   */
  validate: (rule) => {
    return apiClient.post("/distributionrules/validate", rule);
  },
};

export default distributionRulesApi;
